import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { fmtMoney } from '../../../common/utils/money';
import { BILLING_STATUS_LABEL, magnitudeLabel } from '../../../core/constants/billing';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useManagementsList, useDeleteManagement } from '../contexts/managements';
import { useCostsList, useDeleteCost } from '../contexts/costs';
import ManagementForm from '../forms/management-form';
import CostForm from '../forms/cost-form';
import CompositionModal from '../forms/composition-modal';
import TariffModal from '../forms/tariff-modal';
import LiquidationsPanel from './liquidations';

import '../../../common/styles/catalog.css';
import '../styles/billing.css';

function ManagementsPanel() {
    const { data, isLoading, isFetching, error } = useManagementsList({ limit: 500, sort: 'name' });
    const { mutateAsync: remove, isPending: removing } = useDeleteManagement();
    const items = useMemo(() => data?.items ?? [], [data]);
    const [form, setForm] = useState(null);       // null | 'new' | management
    const [composing, setComposing] = useState(null);
    const [tariffFor, setTariffFor] = useState(null);
    const confirm = useConfirm();

    const del = async (m) => {
        const ok = await confirm({
            title: 'Eliminar manejo',
            message: `¿Eliminar el manejo "${m.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(m.id); toast.success('Manejo eliminado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <>
            <div className="billing-overview__bar">
                <Gate domain="billing" action="write">
                    <button type="button" className="billing-overview__new" onClick={() => setForm('new')}><IconPlus /> <span>Nuevo manejo</span></button>
                </Gate>
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver la facturación.' : (error.message || 'No se pudieron cargar los manejos')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando manejos…</div>
            ) : !items.length ? (
                <div className="cat-overview__state"><p className="cat-overview__state-text">Aún no hay manejos.</p></div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead><tr><th>Nombre</th><th>Descripción</th><th>Estado</th><th aria-label="Acciones" /></tr></thead>
                        <tbody>
                            {items.map((m) => (
                                <tr key={m.id} className="cat-table__row">
                                    <td><span className="cat-table__name">{m.name}</span></td>
                                    <td style={{ textAlign: 'left' }}>{m.description || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${m.status}`}>{BILLING_STATUS_LABEL[m.status] || '—'}</span></td>
                                    <td>
                                        <span className="billing-lines__tools">
                                            <button type="button" className="billing-btn billing-btn--ghost billing-btn--sm" onClick={() => setComposing(m)}>Composición</button>
                                            <button type="button" className="billing-btn billing-btn--ghost billing-btn--sm" onClick={() => setTariffFor(m)}>Tarifas</button>
                                            <Gate domain="billing" action="write">
                                                <button type="button" className="billing-icon" onClick={() => setForm(m)} disabled={removing} aria-label="Editar" title="Editar"><IconEdit /></button>
                                                <button type="button" className="billing-icon billing-icon--danger" onClick={() => del(m)} disabled={removing} aria-label="Eliminar" title="Eliminar"><IconTrash /></button>
                                            </Gate>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {form && <ManagementForm management={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
            {composing && <CompositionModal management={composing} onClose={() => setComposing(null)} />}
            {tariffFor && <TariffModal management={tariffFor} onClose={() => setTariffFor(null)} />}
        </>
    );
}

function CostsPanel() {
    const { data, isLoading, isFetching, error } = useCostsList({ limit: 500, sort: 'name' });
    const { mutateAsync: remove, isPending: removing } = useDeleteCost();
    const items = useMemo(() => data?.items ?? [], [data]);
    const [form, setForm] = useState(null);
    const confirm = useConfirm();

    const del = async (c) => {
        const ok = await confirm({
            title: 'Eliminar costo',
            message: `¿Eliminar el costo "${c.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(c.id); toast.success('Costo eliminado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <>
            <div className="billing-overview__bar">
                <Gate domain="billing" action="write">
                    <button type="button" className="billing-overview__new" onClick={() => setForm('new')}><IconPlus /> <span>Nuevo costo</span></button>
                </Gate>
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver la facturación.' : (error.message || 'No se pudieron cargar los costos')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando costos…</div>
            ) : !items.length ? (
                <div className="cat-overview__state"><p className="cat-overview__state-text">Aún no hay costos.</p></div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead><tr><th>Nombre</th><th>Monto</th><th>Escala</th><th>Estado</th><th aria-label="Acciones" /></tr></thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="cat-table__row">
                                    <td><span className="cat-table__name">{c.name}</span></td>
                                    <td>{fmtMoney(c.amount)}</td>
                                    <td>{c.quantitative ? `Por ${magnitudeLabel(c.magnitude)}` : <span className="cat-table__empty">Fija</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${c.status}`}>{BILLING_STATUS_LABEL[c.status] || '—'}</span></td>
                                    <td>
                                        <Gate domain="billing" action="write">
                                            <span className="billing-lines__tools">
                                                <button type="button" className="billing-icon" onClick={() => setForm(c)} disabled={removing} aria-label="Editar" title="Editar"><IconEdit /></button>
                                                <button type="button" className="billing-icon billing-icon--danger" onClick={() => del(c)} disabled={removing} aria-label="Eliminar" title="Eliminar"><IconTrash /></button>
                                            </span>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {form && <CostForm cost={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
        </>
    );
}

// Facturación · Catálogo (Capa 5): manejos + costos + composición (admin-billing-catalog.md).
// El tab activo vive en la URL (?tab=) para que el "volver" del detalle de una liquidación
// aterrice en Liquidaciones y no en Manejos.
const TABS = ['managements', 'costs', 'liquidations'];

export default function BillingCatalog() {
    const [params, setParams] = useSearchParams();
    const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'managements';
    const setTab = (t) => setParams(t === 'managements' ? {} : { tab: t }, { replace: true });
    return (
        <div className="cat-overview">
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Facturación</h1>
                </div>
            </header>

            <div className="billing-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={tab === 'managements'} className={`billing-tab ${tab === 'managements' ? 'billing-tab--active' : ''}`} onClick={() => setTab('managements')}>Manejos</button>
                <button type="button" role="tab" aria-selected={tab === 'costs'} className={`billing-tab ${tab === 'costs' ? 'billing-tab--active' : ''}`} onClick={() => setTab('costs')}>Costos</button>
                <button type="button" role="tab" aria-selected={tab === 'liquidations'} className={`billing-tab ${tab === 'liquidations' ? 'billing-tab--active' : ''}`} onClick={() => setTab('liquidations')}>Liquidaciones</button>
            </div>

            {tab === 'managements' && <ManagementsPanel />}
            {tab === 'costs' && <CostsPanel />}
            {tab === 'liquidations' && <LiquidationsPanel />}
        </div>
    );
}
